import { io, type Socket } from 'socket.io-client';

const TOKEN_KEY = 'guild_mandatary_session';
const USER_KEY = 'guild_mandatary_user';
const SKIP_SHARED_KEY = 'guild_mandatary_skip_shared_session';
const viteEnv = import.meta.env;

export type PortalUser = { userId: string; email: string; name?: string };
type PortalSession = { token: string; user: PortalUser | null };
let memorySession: PortalSession | null = null;
// Après une écriture refusée, la mémoire reste prioritaire tant que le
// stockage ne contient pas une session différente.
let failedWriteToken: string | undefined;

export const socket: Socket = io(viteEnv.VITE_SERVER_URL || undefined, {
  autoConnect: false,
  auth: { buildClient: Number(__APP_BUILD__) },
  transports: ['websocket', 'polling'],
  tryAllTransports: true,
  reconnection: true,
  reconnectionAttempts: 10,
});

function readStoredSession(): PortalSession {
  // Sous /mandataires/, le Bureau partage l'origine du jeu : une personne
  // déjà connectée ne doit pas repasser par un second écran de connexion.
  // Les clés propres au portail restent prioritaires pour préserver son
  // fonctionnement autonome si un sous-domaine est choisi plus tard.
  const portalToken = localStorage.getItem(TOKEN_KEY);
  const token = portalToken || (!localStorage.getItem(SKIP_SHARED_KEY) && localStorage.getItem('guild_sessionToken')) || '';
  // Une fiche seule n'est jamais une session. Ne pas mélanger la fiche du
  // Bureau avec le jeton d'un autre compte connecté dans le jeu.
  let storedUser: PortalUser | null = null;
  try { storedUser = JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { /* Le jeton peut rester valide. */ }
  const user = portalToken ? storedUser : token && (
    localStorage.getItem('guild_deviceId') && localStorage.getItem('guild_accountEmail')
      ? {
          userId: String(localStorage.getItem('guild_deviceId')),
          email: String(localStorage.getItem('guild_accountEmail')),
        }
      : null
  );
  return { token, user: user || null };
}

export function readSession(): PortalSession {
  try {
    const stored = readStoredSession();
    if (memorySession && failedWriteToken !== undefined && stored.token === failedWriteToken) return memorySession;
    failedWriteToken = undefined;
    memorySession = stored;
    return stored;
  } catch {
    return memorySession || { token: '', user: null };
  }
}

function rememberFailedWrite() {
  try { failedWriteToken = readStoredSession().token; }
  catch { failedWriteToken = undefined; }
}

export function isPortalSessionStorageEvent(event: StorageEvent) {
  try {
    return event.storageArea === localStorage && (event.key === null || [
      TOKEN_KEY, SKIP_SHARED_KEY, 'guild_sessionToken',
    ].includes(event.key));
  } catch { return false; }
}

export function subscribeSessionChanges(listener: () => void) {
  const onStorage = (event: StorageEvent) => { if (isPortalSessionStorageEvent(event)) listener(); };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

export function saveSession(token: string, user: PortalUser) {
  memorySession = { token, user };
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.removeItem(SKIP_SHARED_KEY);
    failedWriteToken = undefined;
  } catch { rememberFailedWrite(); }
}

export function clearSession(expectedToken?: string) {
  // Ne pas effacer un compte remplacé dans un autre onglet pendant la requête.
  if (expectedToken !== undefined && readSession().token !== expectedToken) return false;
  memorySession = { token: '', user: null };
  try {
    // Ne pas réimporter le compte du jeu après déconnexion du Bureau.
    localStorage.setItem(SKIP_SHARED_KEY, '1');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    failedWriteToken = undefined;
  } catch { rememberFailedWrite(); }
  return true;
}

export function request<T = any>(event: string, payload?: unknown, timeoutMs = 30000): Promise<T> {
  return requestArgs<T>(event, payload === undefined ? [] : [payload], timeoutMs);
}

export function requestArgs<T = any>(event: string, args: unknown[], timeoutMs = 30000): Promise<T> {
  // Pas de mot de passe ou de mutation en file d'attente hors ligne : un
  // ancien clic ne doit pas être rejoué après le délai affiché à l'utilisateur.
  if (!socket.connected) return Promise.resolve({ error: 'Connexion au serveur interrompue. Réessayez après la reconnexion.', code: 'CONNECTION_UNAVAILABLE' } as T);
  return new Promise((resolve) => {
    socket.timeout(timeoutMs).emit(event, ...args, (error: Error | null, response: T) => {
      resolve(error ? {
        error: socket.connected
          ? 'Le serveur n’a pas confirmé cette demande à temps. Vérifiez son résultat avant de réessayer.'
          : 'Connexion au serveur interrompue. Réessayez après la reconnexion.',
        code: socket.connected ? 'REQUEST_TIMEOUT' : 'CONNECTION_UNAVAILABLE',
      } as T : response);
    });
  });
}

export async function bindPortalSession(session = readSession()) {
  const { token, user } = session;
  if (!token) return { error: 'Aucune session.', code: 'SESSION_INVALID' };
  const result: any = await request('bindSession', { sessionToken: token, deviceId: user?.userId });
  return result;
}

export const portalApi = { socket, readSession, saveSession, clearSession, request, requestArgs, bindPortalSession, subscribeSessionChanges };

export function makeIdempotencyKey(prefix: string) {
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`.slice(0, 96);
}
